/**
 * Real Appointment API Service
 * Fetches actual appointment data from Epic FHIR backend
 */

class AppointmentApiService {
  constructor() {
    const { API_BASE_URL } = require('../config/constants');
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Search appointments using Epic FHIR API
   */
  async searchAppointments(practitionerId = null, dateStart = null, dateEnd = null, limit = 100) {
    try {
      const params = new URLSearchParams();
      
      if (practitionerId) params.append('practitioner_id', practitionerId);
      if (dateStart) params.append('date_start', dateStart);
      if (dateEnd) params.append('date_end', dateEnd);
      if (limit) params.append('limit', limit.toString());

      const response = await fetch(`${this.baseUrl}/api/appointments/search?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return this.transformFHIRAppointments(data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      return [];
    }
  }

  /**
   * Transform FHIR appointment data to our internal format
   */
  transformFHIRAppointments(fhirBundle) {
    if (!fhirBundle?.entry) {
      return [];
    }

    return fhirBundle.entry.map(entry => {
      const appointment = entry.resource;
      const start = new Date(appointment.start);
      const end = new Date(appointment.end);
      const locationName = this.extractLocation(appointment);

      return {
        id: appointment.id,
        start_date: start.toISOString().split('T')[0],
        start_time: start.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        }),
        end_time: end.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        }),
        status: appointment.status,
        description: appointment.description || 'Appointment',
        practitioner: this.extractPractitioner(appointment),
        patient: this.extractPatient(appointment),
        location: locationName,
        hospital: locationName // Alias for compatibility
      };
    });
  }

  /**
   * Extract practitioner information from FHIR appointment
   */
  extractPractitioner(appointment) {
    const practitionerParticipant = appointment.participant?.find(
      p => p.actor?.reference?.startsWith('Practitioner/')
    );

    return {
      id: practitionerParticipant?.actor?.reference,
      name: practitionerParticipant?.actor?.display || 'Unknown Practitioner'
    };
  }

  /**
   * Extract patient information from FHIR appointment
   */
  extractPatient(appointment) {
    const patientParticipant = appointment.participant?.find(
      p => p.actor?.reference?.startsWith('Patient/')
    );

    return {
      id: patientParticipant?.actor?.reference,
      name: patientParticipant?.actor?.display || 'Patient'
    };
  }

  /**
   * Extract location information from FHIR appointment
   */
  extractLocation(appointment) {
    // Handle the location object structure from our FHIR API
    if (appointment.location) {
      if (typeof appointment.location === 'string') {
        return appointment.location;
      } else if (appointment.location.display) {
        return appointment.location.display;
      } else if (appointment.location.name) {
        return appointment.location.name;
      }
    }

    // Fallback to checking participants for location
    const locationParticipant = appointment.participant?.find(
      p => p.actor?.reference?.startsWith('Location/')
    );

    if (locationParticipant?.actor?.display) {
      return locationParticipant.actor.display;
    }

    return 'Unknown'; // Default when no facility info available
  }

  /**
   * Get appointments for multiple practitioners
   */
  async getAppointmentsForPractitioners(practitioners, dateStart = null, dateEnd = null) {
    const allAppointments = [];

    for (const practitioner of practitioners) {
      // Try to get practitioner FHIR ID from saved data
      const practitionerId = practitioner.fhirId || practitioner.npi;
      
      if (practitionerId) {
        const appointments = await this.searchAppointments(
          practitionerId, 
          dateStart, 
          dateEnd, 
          50
        );
        
        // Add practitioner details to each appointment
        const enrichedAppointments = appointments.map(appointment => ({
          ...appointment,
          full_name: practitioner.full_name || practitioner.name,
          specialty: practitioner.specialty,
          hospital: practitioner.hospital || practitioner.organization,
          npi: practitioner.npi,
          physicianId: practitioner.id
        }));
        
        allAppointments.push(...enrichedAppointments);
      }
    }

    return allAppointments.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  }

  /**
   * Generate monthly chart data from real appointments
   */
  generateMonthlyChartFromReal(appointments) {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = new Date().getFullYear();
    const monthlyData = {};

    // Group appointments by month (only current year)
    appointments.forEach(appointment => {
      // Handle both Epic FHIR format (start) and Firebase format (start_date)
      const appointmentDate = appointment.start_date || appointment.start;
      const date = new Date(appointmentDate);
      const appointmentYear = date.getFullYear();
      
      // Only include appointments from current year
      if (appointmentYear === currentYear) {
        const monthKey = `${monthNames[date.getMonth()]} ${appointmentYear}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            month: monthNames[date.getMonth()],
            year: appointmentYear,
            monthKey,
            totalAppointments: 0,
            breakdown: {}
          };
        }

        monthlyData[monthKey].totalAppointments++;

        // Group by physician
        const physicianKey = appointment.physicianId || appointment.full_name;
        
        // Extract proper facility name from API data
        let facilityName = 'Unknown'; // Default when no facility info available
        if (appointment.location && typeof appointment.location === 'string') {
          facilityName = appointment.location;
        } else if (appointment.hospital) {
          facilityName = appointment.hospital;
        } else if (appointment.organizationName) {
          facilityName = appointment.organizationName;
        } else if (appointment.specialty) {
          facilityName = `${appointment.specialty} Center`;
        }
        
        if (!monthlyData[monthKey].breakdown[physicianKey]) {
          monthlyData[monthKey].breakdown[physicianKey] = {
            physicianId: appointment.physicianId,
            physicianName: appointment.full_name,
            specialty: appointment.specialty,
            hospital: facilityName,
            appointmentCount: 0,
            appointments: []
          };
        }

        monthlyData[monthKey].breakdown[physicianKey].appointmentCount++;
        monthlyData[monthKey].breakdown[physicianKey].appointments.push({
          id: appointment.id,
          date: appointment.start_date,
          startTime: appointment.start_time,
          endTime: appointment.end_time,
          procedure: appointment.description || `${appointment.specialty} consultation`,
          hospital: facilityName
        });
      }
    });

    // Convert to array and format breakdown
    return Object.values(monthlyData).map(monthData => ({
      ...monthData,
      breakdown: Object.values(monthData.breakdown).sort((a, b) => b.appointmentCount - a.appointmentCount)
    })).sort((a, b) => {
      // Sort by month order (Jan=0, Feb=1, etc.)
      return monthNames.indexOf(a.month) - monthNames.indexOf(b.month);
    });
  }
}

export default AppointmentApiService; 